import React, {useEffect, useState} from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import NotificationIcon from '@mui/icons-material/Notifications';

import analyticsClient, {AnalyticsEvent} from 'src/services/analytics';
import {Flex} from 'src/components/flex';
import Responsive from 'src/components/responsive/Responsive';
import {usePushSubscription} from 'src/services/push-notifications';
import { getPermission } from 'src/services/push-notifications/utils';
import {usePersistentState} from 'src/services/storage/use-persistent-state';

export default function NotificationAlert() {
    const [initialPermissionFetched, setInitialPermissionFetched] = useState(false);
    const [permission, setPermission] = useState<NotificationPermission | null>(null);
    const [isDismissed, setDismissed] = usePersistentState(
        'isNotificationAlertDismissed',
        false
    );

    const { createPushSubscription } = usePushSubscription();

    const dismissAlert = () => {
        setDismissed(true);
        analyticsClient.track(AnalyticsEvent.NOTIFICATION_ALERT_DISMISSED);
    };

    const requestPermission = async () => {
        const result = await createPushSubscription();
        if (result) {
            setPermission('granted');
            analyticsClient.track(AnalyticsEvent.NOTIFICATION_REQUEST_GRANTED);
        } else {
            setPermission('denied');
            analyticsClient.track(AnalyticsEvent.NOTIFICATION_REQUEST_DENIED);
        }
    };

    useEffect(() => {
        getPermission()
            .then(setPermission)
            .finally(() => setInitialPermissionFetched(true));
    }, [permission]);

    if (!initialPermissionFetched || isDismissed || permission !== 'default') {
        return null;
    }

    return (
        <Responsive desktop>
            <div>
                <Alert
                    severity="info"
                    action={(
                        <Flex alignItems="center" columnGap={2}>
                            <Button
                                variant="outlined"
                                color="dark"
                                onClick={dismissAlert}
                                size="small"
                            >
                                Dismiss
                            </Button>

                            <Button
                                variant="contained"
                                onClick={requestPermission}
                                size="small"
                                startIcon={<NotificationIcon />}
                            >
                                Enable
                            </Button>
                        </Flex>
                    )}
                >
                    You can opt in to receive desktop notifications for new messages.
                </Alert>
            </div>
        </Responsive>
    );
}
